import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { ChangeEvent, useMemo, useState } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import {
  getCollectingCompany,
  getLocationOptions,
  getWasteCategoryOptions
} from 'components/waste-management/api'
import { makeCoreGetFileRequest, makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { downloadCSV, handleQueryError } from 'utils/helpers'
import { Box, Grid } from '@mui/material'
import { Button, IconButton, Input } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { toCompanyPayload } from './serializers'
import { EditCreateModalProps } from 'components/waste-management/interfaces'
import { DownloadIconBlueBackground, FileUploadBlueBackground } from 'icons'
import { formatErrorMessage } from 'utils/formatting'
import { FormFileUpload } from 'components/core-ui/FormFileUpload'
import ls from './CollectingCompanyModal.modules.scss'

const CollectingCompanyModal = (props: EditCreateModalProps) => {
  const { isModalOpen, closeModal, isEdit, editItemId, shouldRefetchItem, setShouldRefreshData } =
    props

  const [file, setFile] = useState<File | null>(null)

  const { showNotification } = useNotification()

  const { data: editItem } = getCollectingCompany(showNotification, editItemId, shouldRefetchItem)
  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)
  const { data: locationOptions } = getLocationOptions(showNotification)

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem])

  const validationSchema = useMemo(() => getValidationSchema(), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toCompanyPayload(values))
    }
  })

  const mutateCompany = async (values: FormData) => {
    editItem
      ? await makeCorePutRequest(`/waste-management-system/company/${editItem.id}`, values)
      : await makeCorePostRequest(`/waste-management-system/company`, values)
  }

  const { isLoading, mutate } = useMutation(mutateCompany, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `Compania a fost ${editItem?.id ? 'editata' : 'creata'} cu succes.`,
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        `A aparut o eroare la ${editItem?.id ? 'editarea companiei.' : 'adaugarea companiei.'}`,
        showNotification
      )
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  const downloadCsv = async () => {
    try {
      const url = isEdit
        ? `/waste-management-system/company/locations/csv?companyId=${editItemId}`
        : '/waste-management-system/company/locations/csv'
      const response = await makeCoreGetFileRequest(url)
      downloadCSV(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const onFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event === null) {
      formik.setFieldValue('metiCodesCsv', null)
      return
    }

    const uploadedFile = event?.target.files && event?.target.files[0]

    if (uploadedFile) {
      const validFileType = uploadedFile.type === 'text/csv' || uploadedFile.name.endsWith('.csv')

      if (!validFileType) {
        showNotification({
          message: 'Fisierul este invalid.',
          type: 'error'
        })
        return
      }

      setFile(uploadedFile)
      formik.setFieldValue('metiCodesCsv', uploadedFile)
    }
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle={isEdit ? 'Editeaza companie' : 'Adauga companie'}
      modalWidth="50%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="name"
            label="Nume"
            fullWidth
            variant="outlined"
            value={formik.values.name}
            onChange={event => formik.setFieldValue('name', event.currentTarget.value)}
            error={!!formik.errors.name}
            helperText={formik.errors.name as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="email"
            label="Email"
            fullWidth
            variant="outlined"
            value={formik.values.email}
            onChange={event => formik.setFieldValue('email', event.currentTarget.value)}
            error={!!formik.errors.email}
            helperText={formik.errors.email as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="contactNumber"
            label="Numar contact"
            fullWidth
            variant="outlined"
            value={formik.values.contactNumber}
            onChange={event => formik.setFieldValue('contactNumber', event.currentTarget.value)}
            error={!!formik.errors.contactNumber}
            helperText={formik.errors.contactNumber as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="contractNumber"
            label="Numar contract"
            fullWidth
            variant="outlined"
            value={formik.values.contractNumber}
            onChange={event => formik.setFieldValue('contractNumber', event.currentTarget.value)}
            error={!!formik.errors.contractNumber}
            helperText={formik.errors.contractNumber as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="tradeRegisterNumber"
            label="Numar registrul comertului"
            fullWidth
            variant="outlined"
            value={formik.values.tradeRegisterNumber}
            onChange={event =>
              formik.setFieldValue('tradeRegisterNumber', event.currentTarget.value)
            }
            error={!!formik.errors.tradeRegisterNumber}
            helperText={formik.errors.tradeRegisterNumber as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="cif"
            label="CIF"
            fullWidth
            variant="outlined"
            value={formik.values.cif}
            onChange={event => formik.setFieldValue('cif', event.currentTarget.value)}
            error={!!formik.errors.cif}
            helperText={formik.errors.cif as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="headquarter"
            label="Sediul central"
            fullWidth
            variant="outlined"
            value={formik.values.headquarter}
            onChange={event => formik.setFieldValue('headquarter', event.currentTarget.value)}
            error={!!formik.errors.headquarter}
            helperText={formik.errors.headquarter as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="county"
            label="Judet"
            fullWidth
            variant="outlined"
            value={formik.values.county}
            onChange={event => formik.setFieldValue('county', event.currentTarget.value)}
            error={!!formik.errors.county}
            helperText={formik.errors.county as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="iban"
            label="IBAN"
            fullWidth
            variant="outlined"
            value={formik.values.iban}
            onChange={event => formik.setFieldValue('iban', event.currentTarget.value)}
            error={!!formik.errors.iban}
            helperText={formik.errors.iban as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="bank"
            label="Banca"
            fullWidth
            variant="outlined"
            value={formik.values.bank}
            onChange={event => formik.setFieldValue('bank', event.currentTarget.value)}
            error={!!formik.errors.bank}
            helperText={formik.errors.bank as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="environmentalPermitNumber"
            label="Autorizatie de mediu nr."
            fullWidth
            variant="outlined"
            value={formik.values.environmentalPermitNumber}
            onChange={event =>
              formik.setFieldValue('environmentalPermitNumber', event.currentTarget.value)
            }
            error={!!formik.errors.environmentalPermitNumber}
            helperText={formik.errors.environmentalPermitNumber as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="environmentalPermitExpirationDate"
            label="Data la care expira autorizatia de mediu"
            fullWidth
            variant="outlined"
            value={formik.values.environmentalPermitExpirationDate}
            onChange={event =>
              formik.setFieldValue('environmentalPermitExpirationDate', event.currentTarget.value)
            }
            error={!!formik.errors.environmentalPermitExpirationDate}
            helperText={formik.errors.environmentalPermitExpirationDate as string}
          />
        </Grid>
        <Grid item xs={12}>
          <Box className={ls.fileBtnsContainer}>
            <Box className={ls.title}>
              <span>Adauga locatii</span>
            </Box>
            <Grid container gap={0}>
              <Grid xs={12} md={6} item className={ls.btnContainer}>
                <IconButton
                  variant="outlined"
                  label="Descarca Template Locatii"
                  startIcon={<DownloadIconBlueBackground />}
                  onClick={downloadCsv}
                />
              </Grid>
              <Grid xs={12} md={6} item className={ls.btnContainer}>
                <FormFileUpload
                  onChange={onFileUpload}
                  icon={<FileUploadBlueBackground />}
                  text="Importa Fisier Locatii"
                  buttonContainerClassName={ls.fileUploadBtn}
                  fileContainerClassName={ls.uploadedFileContainer}
                  showFile
                  accept=".csv"
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={cancelModal} />
          <Button
            variant="contained"
            type="submit"
            label="Salveaza"
            onClick={() => formik.handleSubmit()}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default CollectingCompanyModal
